// Link utilities for Graph click tracking (spec 08 §7).
// Only absolute http(s) links are tracked. mailto:, tel:, javascript:, data:, anchors and the
// unsubscribe link are never collected or rewritten.
import { decodeEntities } from "./htmlToText.ts";

// `\shref` keeps data-href / ng-href out; the value may be double-quoted, single-quoted or bare.
const HREF = /(<a(?=\s)[^>]*?\shref\s*=\s*)("([^"]*)"|'([^']*)'|([^\s"'>]+))/gi;
const escapeAttr = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

export type LinkOptions = { isUnsubscribe?: (href: string) => boolean };

const UNSUB_PATH = /(unsubscribe|d[eé]sabonn|d[eé]sinscri)/i;

function decodedPath(pathname: string): string {
  try {
    return decodeURIComponent(pathname).normalize("NFC");
  } catch {
    return pathname;
  }
}

// Default matcher: the decoded URL path (not the query string) names an unsubscribe page.
export function defaultIsUnsubscribe(href: string): boolean {
  try {
    return UNSUB_PATH.test(decodedPath(new URL(href).pathname));
  } catch {
    return UNSUB_PATH.test(href);
  }
}

// Exact matcher built from the real unsubscribe base URL: same origin plus a path prefix.
export function unsubscribeMatcher(baseUrl: string): (href: string) => boolean {
  const base = new URL(baseUrl);
  const prefix = decodedPath(base.pathname).replace(/\/+$/, "").toLowerCase();
  return (href) => {
    try {
      const u = new URL(href);
      if (u.origin !== base.origin) return false;
      const p = decodedPath(u.pathname).toLowerCase();
      return p === prefix || p.startsWith(`${prefix}/`);
    } catch {
      return false;
    }
  };
}

export function isTrackableLink(href: string, opts?: LinkOptions): boolean {
  if (!/^https?:\/\//i.test(href)) return false;
  return !(opts?.isUnsubscribe ?? defaultIsUnsubscribe)(href);
}

const hrefOf = (g: readonly (string | undefined)[]) =>
  decodeEntities(g[3] ?? g[4] ?? g[5] ?? "");

export function collectLinks(html: string, opts?: LinkOptions): string[] {
  const out: string[] = [];
  for (const m of html.matchAll(HREF)) {
    const href = hrefOf(m);
    if (isTrackableLink(href, opts) && !out.includes(href)) out.push(href);
  }
  return out;
}

// Number of trackable anchors (duplicates counted).
export function countTrackableLinks(html: string, opts?: LinkOptions): number {
  let n = 0;
  for (const m of html.matchAll(HREF))
    if (isTrackableLink(hrefOf(m), opts)) n++;
  return n;
}

export function rewriteLinks(
  html: string,
  map: (href: string) => string | null,
  opts?: LinkOptions,
): string {
  return html.replace(HREF, (...args: unknown[]) => {
    const g = args as string[];
    const href = hrefOf(g);
    if (!isTrackableLink(href, opts)) return g[0];
    const next = map(href);
    return next == null ? g[0] : `${g[1]}"${escapeAttr(next)}"`;
  });
}
